import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  CheckCircle2,
  Mail,
  ArrowRight,
  TrendingUp,
  ShieldCheck,
  Leaf,
} from "lucide-react";
import { sellerAPI } from "../API/axios";

const OTPVerificationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [email, setEmail] = useState("");

  useEffect(() => {
    // Get email from location state or session storage
    const emailFromState = location.state?.email;
    const emailFromStorage = sessionStorage.getItem("pendingVerificationEmail");

    if (emailFromState) {
      setEmail(emailFromState);
    } else if (emailFromStorage) {
      setEmail(emailFromStorage);
    } else {
      toast.error("No email found for verification");
      navigate("/login");
    }
  }, [location.state, navigate]);

  // Cooldown timer
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp?.length < 4) {
      toast.error("Please enter a valid OTP");
      return;
    }

    setLoading(true);
    try {
      await sellerAPI.verifyEmail({ email, otp });
      toast.success("Email verified successfully!");

      // Clear pending verification
      sessionStorage.removeItem("pendingVerificationEmail");

      // Navigate to login with success state
      navigate("/login", { state: { verified: true } });
    } catch (err: any) {
      toast.error(
        err.response?.data?.message || "Invalid OTP. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;

    setResendLoading(true);
    try {
      await sellerAPI.resendOTP({ email });
      setResendCooldown(30);
      toast.success("OTP resent successfully!");
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to resend OTP");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white relative flex flex-col page-root">
      {/* Background Decor */}
      <div className="fixed inset-0 pointer-events-none -z-10">
        <div className="absolute top-0 left-0 w-1/2 h-1/2 bg-primary/5 rounded-full blur-[120px] -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-secondary/3 rounded-full blur-[120px] translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
        {/* Left panel - Hero Side */}
        <div className="hidden lg:flex flex-col p-12 bg-slate-50 border-r border-slate-100">
          <div className="flex items-center gap-4 mb-20 group">
            <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center text-white shadow-xl shadow-primary/20 group-hover:scale-105 transition-transform">
              <Leaf size={26} />
            </div>
            <div className="flex flex-col">
              <div className="font-display text-xl font-bold bg-gradient-to-br from-primary to-secondary bg-clip-text text-transparent">
                Harappa Biosciences
              </div>
              <div className="text-xs text-slate-400 tracking-wider font-medium uppercase">
                Seller Portal
              </div>
            </div>
          </div>

          <div className="grow flex flex-col justify-center">
            <h2 className="font-display text-4xl font-extrabold leading-tight tracking-tight text-slate-900 mb-10">
              Welcome back!
              <br />
              <span className="gradient-text">Your dashboard awaits.</span>
            </h2>

            <div className="flex flex-col gap-8 max-w-md">
              {[
                {
                  icon: TrendingUp,
                  label: "Track orders & revenue in real time",
                  bg: "bg-emerald-50",
                  text: "text-emerald-600",
                },
                {
                  icon: ShieldCheck,
                  label: "Manage GST-verified product listings",
                  bg: "bg-primary/5",
                  text: "text-primary",
                },
                {
                  icon: ArrowRight,
                  label: "Add products and go live instantly",
                  bg: "bg-amber-50",
                  text: "text-amber-600",
                },
              ].map(({ icon: Icon, label, bg, text }) => (
                <div key={label} className="flex items-center gap-5 group">
                  <div
                    className={`w-12 h-12 shrink-0 ${bg} rounded-xl flex items-center justify-center ${text} group-hover:scale-110 transition-transform`}
                  >
                    <Icon size={20} />
                  </div>
                  <span className="text-base font-medium text-slate-600 group-hover:text-slate-900 transition-colors">
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 mt-auto text-xs font-semibold text-slate-400 uppercase tracking-widest">
            <ShieldCheck size={16} className="text-emerald-500" />
            <span>Encrypted · ISO 27001 · SOC 2 Compliant</span>
          </div>
        </div>

        {/* Right panel - OTP verification */}
        <div className="flex items-center justify-center p-6 sm:p-12 sm:pt-24 lg:pt-12">
          <div className="w-full max-w-md animate-slide-up">
            <button
              onClick={() => navigate("/login")}
              className="flex items-center gap-2 text-slate-400 font-bold text-xs uppercase tracking-widest hover:text-primary transition-colors mb-10"
            >
              <ArrowLeft size={16} /> Back to login
            </button>

            <div className="mb-10">
              <h1 className="font-display text-3xl font-extrabold text-slate-900 mb-3 tracking-tight">
                Verify Your Email
              </h1>
              <p className="text-slate-500 text-sm leading-relaxed">
                We've sent a verification code to your email address.
              </p>
            </div>

            <div className="flex items-start gap-4 p-5 bg-primary/5 border border-primary/10 rounded-2xl text-slate-900 mb-10">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Mail size={18} className="text-primary" />
              </div>
              <div className="flex flex-col gap-1">
                <div className="font-bold text-sm tracking-tight">{email}</div>
                <div className="text-xs text-slate-500 leading-relaxed font-medium">
                  Enter the 6-digit OTP sent to this email
                </div>
              </div>
            </div>

            <form onSubmit={handleVerifyOTP} className="flex flex-col gap-8">
              <div className="flex flex-col gap-3 text-center">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-[0.2em]">
                  Verification Code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="form-input !text-4xl !py-5 !tracking-[0.8em] text-center font-mono font-black"
                  placeholder="••••••"
                  value={otp}
                  onChange={(e) =>
                    setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                />
              </div>

              <div className="flex flex-col gap-4">
                <button
                  type="submit"
                  className="btn btn-primary w-full py-4 text-sm font-bold tracking-wide shadow-xl shadow-primary/20"
                  disabled={loading || otp?.length < 4}
                >
                  {loading ? (
                    <div className="flex items-center gap-3">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Verifying...</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span>Verify Email</span>
                      <CheckCircle2 size={18} />
                    </div>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={resendLoading || resendCooldown > 0}
                  className="btn btn-ghost w-full py-4 text-xs font-bold border-none hover:bg-slate-50 transition-all"
                >
                  {resendCooldown > 0 ? (
                    <span className="text-slate-400">
                      Resend OTP in{" "}
                      <span className="text-primary ml-1">
                        {resendCooldown}s
                      </span>
                    </span>
                  ) : resendLoading ? (
                    <div className="flex items-center gap-2 text-slate-400">
                      <div className="w-4 h-4 border-2 border-slate-200 border-t-primary rounded-full animate-spin" />
                      <span>Sending...</span>
                    </div>
                  ) : (
                    "Resend OTP"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTPVerificationPage;
